import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { rowExpandAnimation } from "src/app/animations/row-expand-animation";
import LocalizationConstants from "src/app/constants/localization-constants";
import { Reminder } from "src/app/models/Reminder";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalService } from "src/app/services/modal.service";
import { ReminderService } from "src/app/services/reminder.service";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";
import { ReminderModalComponent } from "../../reminder-modal/reminder-modal.component";
import { RemindersByCompany } from "./../../../models/RemindersByCompany";

@Component({
  selector: "app-company-reminders",
  templateUrl: "./company-reminders.component.html",
  styleUrls: ["./company-reminders.component.scss"],
  animations: [rowExpandAnimation],
  standalone: false,
})
export class CompanyRemindersComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  readonly localizationConstants = LocalizationConstants;
  private subscriptions: Subscription[] = [];
  faSpinner = faSpinner;
  dataSource: MatTableDataSource<RemindersByCompany> = new MatTableDataSource<RemindersByCompany>();

  pageSize: number = 10;

  reminderColumns = [
    { key: "type", title: LocalizationConstants.REMINDER_TYPE },
    { key: "interval", title: LocalizationConstants.REMINDER_INTERVAL },
    { key: "daysBefore", title: LocalizationConstants.REMINDER_DAYS_BEFORE },
    { key: "daysAfter", title: LocalizationConstants.REMINDER_DAYS_AFTER },
    { key: "to", title: LocalizationConstants.REMINDER_TO },
    { key: "cc", title: LocalizationConstants.REMINDER_CC },
    { key: "bcc", title: LocalizationConstants.REMINDER_BCC },
  ];

  reminderColumnKeys = [
    ...this.reminderColumns.map((x) => x.key),
    "reminderAction1",
    "reminderAction2",
  ];

  companyColumnKeys = ["fullName", "companyAction1", "companyAction2"];

  constructor(
    private reminderService: ReminderService,
    private modalService: ModalService,
    private globalOverlayService: GlobalOverlayService,
  ) {}

  ngOnInit(): void {
    this.getReminders();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getDataSource(reminders: Reminder[]): MatTableDataSource<Reminder> {
    return new MatTableDataSource<Reminder>(reminders);
  }

  getReminders() {
    this.globalOverlayService.show();
    this.reminderService
      .getByCompany()
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        const reminders = response;
        // if there are less than 10 companies, then expend them
        if (reminders?.length < 10) {
          reminders.forEach((x) => (x.expanded = true));
        }

        this.dataSource.data = reminders;
      });
  }

  onCreateReminder(company: RemindersByCompany) {
    const reminder = new Reminder(company.name);
    this.openModal(reminder, company);
  }

  onCreateAll(company: RemindersByCompany) {
    this.globalOverlayService.show();
    this.reminderService.getDefaultEmails(company.name).subscribe((emails) => {
      if (!emails) return null;

      this.reminderService
        .createDefaults(company, emails)
        .pipe(finalize(() => this.globalOverlayService.close()))
        .subscribe((defaultReminders) => {
          if (!defaultReminders) return;

          company.reminders.push(...defaultReminders);
        });
    });
  }

  onEditReminder(reminder: Reminder, company: RemindersByCompany) {
    this.openModal(reminder, company);
  }

  private openModal(reminder: Reminder, company: RemindersByCompany) {
    const resultObservable = this.modalService.openDefaultModal(ReminderModalComponent, reminder);

    const subscription = resultObservable.subscribe((result: Reminder) => {
      // modal just closed without saving
      if (!result) {
        return;
      }

      const existingReminderIndex = company.reminders.findIndex((x) => x.id == result.id);

      if (existingReminderIndex === -1) {
        company.reminders.push(result);
      } else {
        company.reminders[existingReminderIndex] = result;
      }
    });

    this.subscriptions.push(subscription);
  }

  private removeReminder(reminder: Reminder) {
    reminder.pending = true;
    this.reminderService
      .delete(reminder.id)
      .pipe(finalize(() => (reminder.pending = false)))
      .subscribe(() => {
        this.dataSource.data.find((x) => x.name == reminder.companyId).reminders =
          this.dataSource.data
            .find((x) => x.name == reminder.companyId)
            .reminders.filter((r) => r.id != reminder.id);
      });
  }

  onRemoveReminder(reminder: Reminder) {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.COMPANY_REMINDER_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.COMPANY_REMINDER_REMOVE_DIALOG_CONTENT,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.removeReminder(reminder);
      }
    });

    this.subscriptions.push(subscription);
  }

  allRemindersCreated(company?: RemindersByCompany) {
    return (
      company?.reminders &&
      this.reminderService.types.filter((x) => !company.reminders.some((r) => r.type === x.key))
        .length === 0
    );
  }

  public toggleExpandRow(company: RemindersByCompany) {
    company.expanded = !company.expanded;
  }

  public shouldRenderReminderRow(index: number, company: RemindersByCompany): boolean {
    return company.reminders.length > 0;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
